import moment from 'moment'

import type {
	AggregateFeedConsumptionResult,
	FeedConsumptionChartPoint,
	FeedConsumptionGroupItem,
} from '../types'

/**
 * Aggregates all groups' dots by date and fills missing days in range.
 * Returns fetchData in the format expected by GroupApexConsuption.
 */
export function aggregateAllGroupsToFetchData(
	groups: FeedConsumptionGroupItem[],
	startDate: number,
	endDate: number,
): AggregateFeedConsumptionResult {
	const byDate = new Map<
		number,
		{
			distributed_sum: number
			cleaned_sum: number
			cattle_count: number
			psvSum: number
			psvCount: number
		}
	>()

	for (const group of groups) {
		if (!Array.isArray(group.dots)) continue
		for (const dot of group.dots) {
			const dateMs = moment(dot.date).startOf('day').valueOf()
			const existing = byDate.get(dateMs)
			const distributed = dot.distributed_sum ?? 0
			const cleaned = dot.cleaned_sum ?? 0
			const cattle = dot.cattle_count ?? 0
			const psv = dot.psv ?? 0

			if (existing) {
				existing.distributed_sum += distributed
				existing.cleaned_sum += cleaned
				existing.cattle_count += cattle
				existing.psvSum += psv
				existing.psvCount += 1
			} else {
				byDate.set(dateMs, {
					distributed_sum: distributed,
					cleaned_sum: cleaned,
					cattle_count: cattle,
					psvSum: psv,
					psvCount: 1,
				})
			}
		}
	}

	const aggregated: FeedConsumptionChartPoint[] = []
	Array.from(byDate.entries()).forEach(([date, agg]) => {
		aggregated.push({
			date,
			distributed_sum: agg.distributed_sum,
			cleaned_sum: agg.cleaned_sum,
			cattle_count: agg.cattle_count,
			psv: agg.psvCount > 0 ? agg.psvSum / agg.psvCount : 0,
		})
	})
	aggregated.sort((a, b) => a.date - b.date)

	const startDay = moment(startDate).startOf('day')
	const endDay = moment(endDate).startOf('day')
	const fetchData: FeedConsumptionChartPoint[] = []

	for (let d = startDay.clone(); d.isSameOrBefore(endDay); d.add(1, 'day')) {
		const date = d.valueOf()
		const point = aggregated.find(p => p.date === date)
		if (point) {
			fetchData.push({ ...point })
		} else {
			fetchData.push({
				date,
				distributed_sum: null,
				cleaned_sum: null,
				cattle_count: 0,
				psv: 0,
			})
		}
	}

	let distributedFood = 0
	let consumptionFood = 0
	for (const p of fetchData) {
		distributedFood += p.distributed_sum ?? 0
		consumptionFood += p.cleaned_sum ?? 0
	}

	return {
		fetchData,
		distributedFood: Number(distributedFood.toFixed(0)),
		consumptionFood: Number(consumptionFood.toFixed(0)),
	}
}
